import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import Loading from "components/Loading";
import { getProducts, getProductTrend } from "actions/spyAdvancedActions";
import useCallApi from "hook/useCallApi";
import { getQueryParams } from "components/Function";
import { ReactTableDataGrid } from "components/Table";
import SpyFilter from "../filter";
import ModalSearchImage from "./modalSearchImage";
import { heightRow, CATEGORIES } from "../constant";
import useColumnTable from "../hooks/useColumnTableProduct";

const limit = 20;

const SpyAdvancedProducts = ({ getProducts, getProductTrend }) => {
    const { filter: initialFilter, page: initialPage } = getQueryParams();
    const [filter, setFilter] = useState(initialFilter);
    const [search, setSearch] = useState(initialFilter.keyword || "");
    const [page, setPage] = useState(initialPage);
    const params = useMemo(() => ({ ...filter, page, per_page: limit }), [filter, page]);
    const { data, loading } = useCallApi(getProducts, params);
    const productIds = useMemo(() => {
        return data?.data?.map((item) => item.product_id).join(",");
    }, [data]);
    const { data: productTrendData } = useCallApi(getProductTrend, productIds, Boolean(productIds));
    const columns = useColumnTable(heightRow, productTrendData, filter.dateRange);

    useEffect(() => {
        const cleanedFilter = Object.fromEntries(
            Object.entries(filter).filter(([_, value]) => value)
        );
        const params = new URLSearchParams({ ...cleanedFilter, page });
        window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    }, [filter, page]);

    const onTableChanged = ({ page }) => {
        if (page) setPage(page);
    };

    return (
        <div className="card mb-5 mb-xl-10">
            <Loading isLoading={loading} />
            <div className="card-body">
                <div className="row mb-10">
                    <div className="col-md-4 d-flex align-items-center">
                        <h1 className="mb-0">Product</h1>
                    </div>
                    <div className="col-md-8 position-relative">
                        <div className="input-group h-100">
                            <input
                                type="text"
                                className="form-control rounded-end rounded-pill"
                                placeholder="Search..."
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <ModalSearchImage
                                onSearch={(image, text) => {
                                    setFilter((prev) => ({ ...prev, image, keyword: text }));
                                    setPage(1);
                                }}
                            />
                            <button
                                className="btn btn-info rounded-start rounded-pill border-start"
                                style={{ borderLeftColor: "#ececec" }}
                                type="button"
                                onClick={() => {
                                    setFilter((prev) => ({ ...prev, keyword: search }));
                                    setPage(1);
                                }}
                            >
                                <i className="fas fa-search"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <SpyFilter
                    filter={filter}
                    setFilter={(data) => {
                        setFilter(data);
                        setPage(1);
                    }}
                    filterCategory={[{
                        label: "Product Category",
                        key: "product_categories",
                        data: CATEGORIES,
                    }]}
                    filterShows={[
                        { label: "Region", value: "region", defaultValue: "US" },
                        { label: "Price", value: "price" },
                        { label: "Commission Rate", value: "commission_rate" },
                        { label: "Influencer", value: "related_influencers" },
                        { label: "Videos", value: "videos_count" },
                        { label: "Video Views", value: "views_count" },
                        { label: "Product Experience Points", value: "product_rating" },
                        { label: "Comments", value: "review_count" },
                    ]}
                    filterAdvancedShows={[
                        { label: "Main Sales Methods", value: "sales_flag" },
                        { label: "Sales trends", value: "sales_trend_flag" },
                        { label: "S Shop", value: "is_s_shop" },
                        { label: "Free shipping", value: "is_free_shipping" },
                        {
                            label: "Sales Volume",
                            value: [
                                { label: "30-day sales volume", value: "sales" },
                                { label: "Total Sale", value: "total_sale_cnt" },
                            ],
                            defaultValue: "sales",
                            type: "group",
                        },
                        {
                            label: "Estimated GMV",
                            value: [
                                { label: "30-day GMV", value: "gmv_amt_30d" },
                                { label: "Total GMV", value: "gmv" },
                            ],
                            defaultValue: "gmv_amt_30d",
                            type: "group",
                        },
                        { label: "Estimated listing time", value: "est_date", type: "date_picker" },
                        { label: "Date Range", value: "dateRange", defaultValue: "30" },
                    ]}
                />
                <div className="mt-10">
                    <ReactTableDataGrid
                        tableName="productSpyAdvanced"
                        idProperty="_id"
                        columns={columns}
                        rowData={data?.data || []}
                        pageNumber={page}
                        gridOptions={{
                            rowHeight: heightRow,
                            reactiveCustomComponents: true,
                        }}
                        paginationPageSize={limit}
                        totalSize={data?.meta?.total || 0}
                        emptyText={"No data!"}
                        isSearchText={false}
                        onTableChanged={onTableChanged}
                    />
                </div>
            </div>
        </div>
    );
};

export default connect(null, { getProducts, getProductTrend })(SpyAdvancedProducts);
